// Treść z serwera (GET /api/content) z awaryjnym zestawem wbudowanym w aplikację. Od patcha 49 to wyłącznie linijki bota (`banter`): lekcje kursu są wbudowane w aplikację
// (src/content/levels/*.json), a dawne „dni” z bazy D1 zniknęły razem z panelem, który je edytował.
// Zasady, które mają chronić dziecko przed pustym ekranem:
//   1. Przy starcie używamy ostatniej poprawnej treści z pamięci przeglądarki, a gdy jej nie ma — wbudowanej (src/content/banter.json).
//   2. Nowszą wersję pobieramy w tle; każdą odpowiedź sprawdzamy pod kątem budowy — zepsutej, pustej albo „nie-JSON” (np. strona HTML
//      z hostingu bez backendu) nie przyjmujemy, zostaje to, co było.
//   3. Brak sieci, 404, 503 → po cichu zostaje bieżąca treść. Błąd serwera nigdy nie dociera do dziecka.
// Starsza treść zapisana w przeglądarce (z polami `lessons` i `audio`) nadal przechodzi sprawdzenie — tych pól po prostu nie czytamy.
import type { BanterLine } from '../types.ts';
import type { KeyValueStore } from '../lib/progress.ts';

export const CONTENT_CACHE_KEY = 'naative.content.v1';

export interface RemoteContent {
  version: string;
  banter: BanterLine[];
}

const isObj = (x: unknown): x is Record<string, unknown> => typeof x === 'object' && x !== null && !Array.isArray(x);
const isStr = (x: unknown): x is string => typeof x === 'string';
const filled = (x: unknown): x is string => isStr(x) && x.trim().length > 0;

/** Pary „kategoria|etap” używane przez aplikację, wyliczone z wbudowanych linijek bota (pusty etap = dowolny). */
export function banterNeeds(lines: readonly BanterLine[]): string[] {
  return [...new Set(lines.map((b) => `${b.category}|${b.step ?? ''}`))];
}

export interface ContentNeeds {
  /** Co bot musi mieć do powiedzenia (patrz `banterNeeds`). Brakująca kategoria wywaliłaby lekcję w środku (pickBanter rzuca wyjątek), więc taką treść odrzucamy. */
  banter?: readonly string[];
}

/** Sprawdza budowę linijek bota (nie sens językowy). Zwraca null, gdy cokolwiek się nie zgadza. */
export function validateContent(x: unknown, needs: ContentNeeds = {}): RemoteContent | null {
  if (!isObj(x) || !isStr(x.version) || !Array.isArray(x.banter) || x.banter.length === 0) return null;
  const ids = new Set<string>();
  for (const b of x.banter) {
    if (!isObj(b) || !filled(b.id) || ids.has(b.id) || !filled(b.category) || !filled(b.text) || (b.lang !== 'en' && b.lang !== 'pl')) return null;
    if (b.pl !== undefined && !isStr(b.pl)) return null;
    if (b.step !== undefined && !isStr(b.step)) return null;
    ids.add(b.id);
  }
  for (const need of needs.banter ?? []) {
    const [category, step] = need.split('|');
    if (!(x.banter as Record<string, unknown>[]).some((b) => b.category === category && (!step || b.step === step))) return null;
  }
  return { version: x.version, banter: x.banter as BanterLine[] };
}

/** Ostatnia poprawna treść zapisana w przeglądarce (albo null). */
export function loadCachedContent(store: KeyValueStore | null, needs: ContentNeeds = {}): RemoteContent | null {
  try {
    const raw = store?.getItem(CONTENT_CACHE_KEY);
    return raw ? validateContent(JSON.parse(raw), needs) : null;
  } catch {
    return null;
  }
}

export function saveCachedContent(store: KeyValueStore | null, content: RemoteContent): boolean {
  try {
    if (!store) return false;
    store.setItem(CONTENT_CACHE_KEY, JSON.stringify(content));
    return true;
  } catch {
    return false; // pełna pamięć albo tryb prywatny — treść zadziała do końca sesji, a następnym razem pobierzemy ją znów
  }
}

export type SyncResult =
  | { kind: 'updated'; content: RemoteContent }
  | { kind: 'unchanged' }
  | { kind: 'skipped'; why: string };

/**
 * Pyta serwer o treść nowszą niż `currentVersion`. Nigdy nie rzuca wyjątku. Zapisuje do pamięci tylko poprawną treść.
 * `updated` oznacza, że `content` jest nowa i poprawna.
 */
export async function syncContent(
  currentVersion: string,
  store: KeyValueStore | null,
  fetchImpl: typeof fetch = (...args) => fetch(...args),
  url = './api/content',
  needs: ContentNeeds = {},
): Promise<SyncResult> {
  try {
    const res = await fetchImpl(currentVersion ? `${url}?since=${encodeURIComponent(currentVersion)}` : url, { cache: 'no-store' });
    if (!res.ok) return { kind: 'skipped', why: `HTTP ${res.status}` };
    const body: unknown = await res.json();
    if (isObj(body) && body.unchanged === true) return { kind: 'unchanged' };
    const content = validateContent(body, needs);
    if (!content) return { kind: 'skipped', why: 'odpowiedź nie wygląda na treść aplikacji' };
    if (content.version === currentVersion) return { kind: 'unchanged' };
    saveCachedContent(store, content);
    return { kind: 'updated', content };
  } catch (e) {
    return { kind: 'skipped', why: e instanceof Error ? e.message : String(e) };
  }
}
